import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { PunchEvent } from '../types/employee';
import { loadPunchEvents, savePunchEvents } from '../services/punchStorage';
import { EmployeeSourceProvider, type EmployeeSource } from './employeeSource';
import { useOfficeAutoLogout } from './workforce/useOfficeAutoLogout';
import { useWorkforcePunchActions } from './workforce/useWorkforcePunchActions';
import { useWorkforceScheduleQueries } from './workforce/useWorkforceScheduleQueries';
import { useWorkforceSession } from './workforce/useWorkforceSession';
import type { WorkforceState } from './workforce/workforceTypes';

export type {
  CorrectionResult,
  LoginResult,
  OfficeAutoLogout,
  OfficeAutoLogoutReason,
  PunchResult,
  WorkforceState,
} from './workforce/workforceTypes';

const WorkforceContext = createContext<WorkforceState | null>(null);

/**
 * Employees come from the source (Convex when configured, otherwise
 * localStorage), while punch events remain local. Keeping that split here lets
 * the session/punch/schedule hooks stay unaware of where employees live.
 */
export function WorkforceProvider({ children }: { children: React.ReactNode }) {
  return (
    <EmployeeSourceProvider>
      {(source) => <WorkforceProviderInner source={source}>{children}</WorkforceProviderInner>}
    </EmployeeSourceProvider>
  );
}

function WorkforceProviderInner({
  source,
  children,
}: {
  source: EmployeeSource;
  children: React.ReactNode;
}) {
  const {
    employees,
    refreshEmployees,
    createEmployeeProfile,
    updateEmployeeProfile,
    setEmployeePin: setEmployeePinOnSource,
  } = source;

  const [punchEvents, setPunchEvents] = useState<PunchEvent[]>([]);
  const [session, setSession] = useState<WorkforceState['session']>(null);
  const [scheduleTick, setScheduleTick] = useState(0);
  const [punchRevision, setPunchRevision] = useState(0);
  const [storageHydrated, setStorageHydrated] = useState(false);
  const skipNextPunchSave = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPunchEvents(loadPunchEvents());
    setStorageHydrated(true);
  }, []);

  useEffect(() => {
    if (!storageHydrated) return;
    if (skipNextPunchSave.current) {
      skipNextPunchSave.current = false;
      return;
    }
    savePunchEvents(punchEvents, { notify: false });
  }, [punchEvents, storageHydrated]);

  const { activeEmployee, login, establishSession, logout, setEmployeePin } =
    useWorkforceSession(employees, setEmployeePinOnSource, session, setSession);

  const {
    punch,
    voidPunch,
    addCorrectionPunch,
    addMissingPunches,
    replacePunch,
    replaceDayPunchRange,
  } = useWorkforcePunchActions(session, employees, punchEvents, setPunchEvents);

  const {
    getTodayStatus,
    getTodayEvents,
    getWeekPunchRecords: getWeekPunchRecordsForEmployee,
    getWeekSchedule,
    getWeekSummary,
    hasShiftsAssigned,
    refreshScheduleData,
    refreshPunchData,
  } = useWorkforceScheduleQueries(
    session,
    punchEvents,
    setPunchEvents,
    scheduleTick,
    setScheduleTick,
    setPunchRevision,
    employees
  );

  const officeAutoLogout = useOfficeAutoLogout(session, logout);

  const value: WorkforceState = {
    employees,
    punchEvents,
    session,
    activeEmployee,
    login,
    establishSession,
    logout,
    setEmployeePin,
    punch,
    voidPunch,
    addCorrectionPunch,
    addMissingPunches,
    replacePunch,
    replaceDayPunchRange,
    getTodayStatus,
    getTodayEvents,
    getWeekPunchRecords: getWeekPunchRecordsForEmployee,
    getWeekSchedule,
    getWeekSummary,
    hasShiftsAssigned,
    refreshScheduleData,
    refreshPunchData,
    refreshEmployees,
    createEmployeeProfile,
    updateEmployeeProfile,
    officeAutoLogout,
    punchRevision,
    scheduleRevision: scheduleTick,
  };

  return (
    <WorkforceContext.Provider value={value}>
      {children}
    </WorkforceContext.Provider>
  );
}

export function useWorkforce() {
  const ctx = useContext(WorkforceContext);
  if (!ctx) throw new Error('useWorkforce must be used within WorkforceProvider');
  return ctx;
}
